// CandidateOfferLetter.jsx - FIXED VERSION WITH PDF INTEGRATION
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './CandidateOfferLetter.css';

// Professional Signature Pad Component
const ProfessionalSignaturePad = ({ onSave, onClose, candidateName }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureStyle, setSignatureStyle] = useState('cursive');
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    initializeCanvas();
  }, [signatureStyle]);

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext('2d');
      setCtx(context);
      
      // Set canvas dimensions based on device pixel ratio for better quality
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      
      context.scale(dpr, dpr);
      
      // Clear canvas
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add guide lines
      context.strokeStyle = '#f0f0f0';
      context.lineWidth = 0.5;
      
      // Horizontal lines
      for(let i = 20; i < canvas.height / dpr; i += 20) {
        context.beginPath();
        context.moveTo(0, i);
        context.lineTo(canvas.width / dpr, i);
        context.stroke();
      }
      
      // Vertical lines
      for(let i = 50; i < canvas.width / dpr; i += 50) {
        context.beginPath();
        context.moveTo(i, 0);
        context.lineTo(i, canvas.height / dpr);
        context.stroke();
      }
      
      // Set signature style
      switch(signatureStyle) {
        case 'cursive':
          context.strokeStyle = '#1e40af';
          context.lineWidth = 3;
          context.lineCap = 'round';
          context.lineJoin = 'round';
          break;
        case 'formal':
          context.strokeStyle = '#0f172a';
          context.lineWidth = 2;
          context.lineCap = 'square';
          context.lineJoin = 'miter';
          break;
        case 'digital':
          context.strokeStyle = '#059669';
          context.lineWidth = 2.5;
          context.lineCap = 'round';
          context.lineJoin = 'round';
          break;
      }
    }
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    if (e.type.includes('touch')) {
      return {
        x: (e.touches[0].clientX - rect.left) * (canvas.width / rect.width / dpr),
        y: (e.touches[0].clientY - rect.top) * (canvas.height / rect.height / dpr)
      };
    } else {
      return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width / dpr),
        y: (e.clientY - rect.top) * (canvas.height / rect.height / dpr)
      };
    }
  };

  const startDrawing = (e) => {
    e.preventDefault();
    if (!ctx) return;
    
    const { x, y } = getCanvasCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing || !ctx) return;
    
    const { x, y } = getCanvasCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    initializeCanvas();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Create a new canvas with white background for cleaner signature
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    // Set dimensions
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    
    // Fill with white background
    tempCtx.fillStyle = '#ffffff';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    
    // Draw original signature
    tempCtx.drawImage(canvas, 0, 0);
    
    // Convert to data URL
    const signatureData = tempCanvas.toDataURL('image/png');
    onSave(signatureData);
    onClose();
  };

  const generateDigitalSignature = () => {
    if (!ctx || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const context = ctx;
    
    // Clear canvas first
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set font based on style
    let fontFamily = '"Brush Script MT", cursive';
    let fontSize = 40;
    let color = '#1e40af';
    
    switch(signatureStyle) {
      case 'cursive':
        fontFamily = '"Brush Script MT", cursive';
        fontSize = 42;
        color = '#1e40af';
        break;
      case 'formal':
        fontFamily = '"Times New Roman", Times, serif';
        fontSize = 36;
        color = '#0f172a';
        break;
      case 'digital':
        fontFamily = '"Courier New", monospace';
        fontSize = 38;
        color = '#059669';
        break;
    }
    
    context.font = `italic ${fontSize}px ${fontFamily}`;
    context.fillStyle = color;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    
    // Add name
    context.fillText(candidateName, canvas.width / 2, canvas.height / 2);
    
    // Add decorative underline for formal style
    if (signatureStyle === 'formal') {
      const textWidth = context.measureText(candidateName).width;
      context.beginPath();
      context.moveTo((canvas.width - textWidth) / 2, canvas.height / 2 + 20);
      context.lineTo((canvas.width + textWidth) / 2, canvas.height / 2 + 20);
      context.lineWidth = 1.5;
      context.strokeStyle = color;
      context.stroke();
    }
  };

  useEffect(() => {
    // Add event listeners for touch device support
    const canvas = canvasRef.current;
    if (!canvas) return;

    const preventScroll = (e) => e.preventDefault();

    canvas.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  return (
    <div className="professional-signature-modal">
      <div className="signature-modal-content">
        <div className="signature-modal-header">
          <h3>✍️ Digital Signature</h3>
          <button onClick={onClose} className="close-signature-btn">×</button>
        </div>
        
        <div className="signature-style-selector">
          <p>Signature Style:</p>
          <div className="style-buttons">
            <button 
              className={`style-btn ${signatureStyle === 'cursive' ? 'active' : ''}`}
              onClick={() => setSignatureStyle('cursive')}
            >
              🖋️ Cursive
            </button>
            <button 
              className={`style-btn ${signatureStyle === 'formal' ? 'active' : ''}`}
              onClick={() => setSignatureStyle('formal')}
            >
              📝 Formal
            </button>
            <button 
              className={`style-btn ${signatureStyle === 'digital' ? 'active' : ''}`}
              onClick={() => setSignatureStyle('digital')}
            >
              💻 Digital
            </button>
          </div>
        </div>
        
        <div className="signature-canvas-container">
          <div className="canvas-header">
            <span className="canvas-label">Sign here</span>
            <button 
              onClick={generateDigitalSignature}
              className="generate-signature-btn"
            >
              🎨 Generate Digital Signature
            </button>
          </div>
          
          <canvas
            ref={canvasRef}
            className="professional-signature-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          
          <div className="canvas-instructions">
            <p>• Draw your signature using mouse/touch</p>
            <p>• Click "Generate" for a typed signature</p>
            <p>• Click "Clear" to start over</p>
          </div>
        </div>
        
        <div className="signature-modal-actions">
          <div className="signature-action-buttons">
            <button onClick={clearSignature} className="clear-signature-btn">
              🗑️ Clear
            </button>
            <button onClick={saveSignature} className="save-signature-btn">
              ✅ Save & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Candidate Offer Letter Component
const CandidateOfferLetter = () => {
  const { offerLetterId } = useParams();
  const [offerLetter, setOfferLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [candidateSignature, setCandidateSignature] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [declaration, setDeclaration] = useState({
    termsAccepted: false,
    backgroundCheck: false,
    confidentiality: false,
    documentAuthenticity: false
  });
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Load offer letter from backend
  useEffect(() => {
const fetchOfferLetter = async () => {
  try {
    setLoading(true);
    console.log('🔄 Fetching offer letter from backend...');
    console.log('📡 API URL:', `${API_BASE_URL}/api/offer-letters/${offerLetterId}`);
    
    // Try BOTH endpoints to see which works
    const response = await fetch(`${API_BASE_URL}/api/offer-letters/${offerLetterId}`);
    
    console.log('📊 Response Status:', response.status, response.statusText);
    console.log('📊 Response Headers:', {
      'Content-Type': response.headers.get('Content-Type'),
      'Content-Length': response.headers.get('Content-Length')
    });
    
    if (!response.ok) {
      // Read response as text first to see what we got
      const responseText = await response.text();
      console.log('❌ Response Content (first 500 chars):', responseText.substring(0, 500));
      
      if (responseText.includes('<!DOCTYPE') || responseText.includes('<html>')) {
        // Server returned HTML, not JSON
        console.error('❌ SERVER ERROR: Backend returned HTML instead of JSON');
        console.error('❌ This means the route /api/offer-letters/:id is not properly defined');
        
        // Try alternative endpoint as fallback
        console.log('🔄 Trying alternative endpoint...');
        const altResponse = await fetch(`${API_BASE_URL}/api/offer-letter/${offerLetterId}`);
        
        if (altResponse.ok) {
          const altResult = await altResponse.json();
          console.log('✅ Alternative endpoint worked!');
          processOfferData(altResult.data);
          return;
        }
        
        throw new Error(`Backend routing error: Server returned HTML page instead of JSON data. Please check if route GET /api/offer-letters/:id exists.`);
      } else {
        // Try to parse as JSON error
        try {
          const errorJson = JSON.parse(responseText);
          throw new Error(errorJson.message || `Server error ${response.status}`);
        } catch {
          throw new Error(`Server error ${response.status}: ${responseText.substring(0, 100)}`);
        }
      }
    }
    
    // Check content type
    const contentType = response.headers.get('Content-Type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('❌ Wrong Content-Type:', contentType);
      console.error('❌ Response (first 300 chars):', responseText.substring(0, 300));
      throw new Error(`Server returned ${contentType || 'unknown'} instead of JSON. Check backend routing.`);
    }
    
    const result = await response.json();
    console.log('✅ API Response received:', result);
    
    if (result.success && result.data) {
      processOfferData(result.data);
    } else {
      throw new Error(result.message || 'Invalid response from server');
    }
  } catch (error) {
    console.error('❌ Error loading offer letter:', error);
    setError(error.message || 'Failed to load offer letter. Please check the link or contact HR.');
  } finally {
    setLoading(false);
  }
};

// Helper function to process offer data
const processOfferData = (data) => {
  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  // Create formatted offer letter data
  const formattedData = {
    _id: data._id,
    candidateName: data.candidateName,
    candidateEmail: data.candidateEmail,
    candidateAddress: data.candidateAddress,
    position: data.position,
    department: data.department,
    salary: data.salary,
    ctc: data.ctc,
    salaryPeriod: data.salaryPeriod,
    joiningDate: formatDate(data.joiningDate),
    workLocation: data.workLocation,
    reportingManager: data.reportingManager,
    probationPeriod: data.probationPeriod,
    noticePeriod: data.noticePeriod,
    workingHours: data.workingHours,
    saturdayHours: data.saturdayHours,
    employeeCode: data.employeeCode,
    refNumber: data.refNumber,
    appointmentLetterNumber: data.appointmentLetterNumber,
    expiresAt: formatDate(data.expiresAt),
    status: data.status,
    offerValidityDays: data.offerValidityDays,
    candidateSignature: data.candidateSignature,
    // Include offerData structure
    offerData: {
      candidateName: data.candidateName,
      candidateEmail: data.candidateEmail,
      position: data.position,
      department: data.department,
      salary: data.salary,
      ctc: data.ctc,
      basicSalary: data.salaryBreakup?.basicSalary,
      hra: data.salaryBreakup?.hra,
      specialAllowance: data.salaryBreakup?.specialAllowance,
      conveyanceAllowance: data.salaryBreakup?.conveyanceAllowance,
      medicalAllowance: data.salaryBreakup?.medicalAllowance,
      grossSalary: data.salaryBreakup?.grossSalary,
      workingHours: data.workingHours,
      saturdayHours: data.saturdayHours,
      probationPeriod: data.probationPeriod,
      noticePeriod: data.noticePeriod,
      employeeCode: data.employeeCode,
      offerReference: data.refNumber,
      workLocation: data.workLocation,
      reportingManager: data.reportingManager
    },
    // Include benefits and leavePolicy
    benefits: data.benefits || {},
    leavePolicy: data.leavePolicy || {},
    hrSignature: data.hrSignature,
    candidateSignature: data.candidateSignature,
    companyStamp: data.companyStamp,
    // Company data from companyDetails
    companyData: data.companyDetails || {}
  };
  
  setOfferLetter(formattedData);
  
  // Set candidate signature if already signed
  if (data.candidateSignature) {
    setCandidateSignature(data.candidateSignature);
  }
  
  console.log('✅ Offer letter loaded successfully:', formattedData.candidateName);
};

    if (offerLetterId) {
      fetchOfferLetter();
    } else {
      setError('No offer letter ID provided');
      setLoading(false);
    }
  }, [offerLetterId, API_BASE_URL]);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '';
    try {
      // Extract numbers from string like "₹90,000 per month"
      const numStr = amount.toString().replace(/[^\d]/g, '');
      const num = parseInt(numStr);
      if (isNaN(num)) return amount;
      
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    } catch {
      return amount;
    }
  };

  // Handle offer acceptance
// Handle offer acceptance
// CORRECTED handleAcceptOffer function
const handleAcceptOffer = async () => {
  if (!candidateSignature) {
    alert('Please sign the document before accepting');
    return;
  }

  if (!declaration.termsAccepted || !declaration.backgroundCheck || 
      !declaration.confidentiality || !declaration.documentAuthenticity) {
    alert('Please accept all declarations before proceeding');
    return;
  }

  setIsSubmitting(true);
  
  try {
    console.log('🔄 Accepting offer...');
    
    // FIXED ENDPOINT - Send to the correct route
    const response = await fetch(`${API_BASE_URL}/api/offer-letters/accept`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        offerLetterId, // Include ID in body
        candidateSignature,
        candidateName: offerLetter?.candidateName,
        candidateEmail: offerLetter?.candidateEmail,
        declarations: declaration
      })
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('❌ Server error response:', errorData);
      
      // Try alternative endpoint if first fails
      const altResponse = await fetch(`${API_BASE_URL}/api/offer-letters/${offerLetterId}/accept`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidateSignature,
          candidateName: offerLetter?.candidateName,
          candidateEmail: offerLetter?.candidateEmail,
          declarations: declaration
        })
      });
      
      if (!altResponse.ok) {
        throw new Error(`Server error ${altResponse.status}`);
      }
      
      const altResult = await altResponse.json();
      console.log('✅ Acceptance response (alt):', altResult);

      if (altResult.success) {
        // Update local state
        setOfferLetter(prev => ({
          ...prev,
          status: 'accepted',
          candidateSignature
        }));
        
        setShowAcceptConfirmation(true);
        await sendAcceptanceConfirmation();
      } else {
        throw new Error(altResult.message || 'Failed to accept offer');
      }
      
    } else {
      const result = await response.json();
      console.log('✅ Acceptance response:', result);

      if (result.success) {
        // Update local state
        setOfferLetter(prev => ({
          ...prev,
          status: 'accepted',
          candidateSignature
        }));
        
        setShowAcceptConfirmation(true);
        await sendAcceptanceConfirmation();
        
      } else {
        alert(`❌ Failed to accept offer: ${result.message}`);
      }
    }
  } catch (error) {
    console.error('❌ Error accepting offer:', error);
    alert('❌ Error accepting offer. Please try again or contact HR.');
  } finally {
    setIsSubmitting(false);
  }
};

  // Download PDF using backend Puppeteer
  const downloadPDF = async () => {
    try {
      setDownloadingPDF(true);
      
      // Use the backend PDF route
      const response = await fetch(`${API_BASE_URL}/api/pdf/offer-letter/${offerLetterId}/pdf`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to generate PDF: ${response.status}`);
      }

      // Get the PDF blob
      const pdfBlob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Offer_Letter_${offerLetter.candidateName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again or contact HR.');
      
      // Fallback to client-side PDF generation
      alert('Using alternative PDF generation...');
      generateClientSidePDF();
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Client-side PDF fallback
  const generateClientSidePDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to download PDF');
      return;
    }

    const pdfContent = generatePDFContent();
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 500);
  };

  // Generate PDF content for client-side fallback
  const generatePDFContent = () => {
    if (!offerLetter) return '';
    
    const offerData = offerLetter.offerData || {};
    const companyData = offerLetter.companyData || {};

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Appointment Letter - ${offerData.candidateName || 'Candidate'}</title>
       
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Inter', sans-serif; 
            margin: 40px; 
            line-height: 1.6; 
            color: #1e293b;
            font-size: 12px;
            background: #ffffff;
          }
          
          .letter-container {
            max-width: 800px;
            margin: 0 auto;
            border: 2px solid #e2e8f0;
            padding: 40px;
            background: #ffffff;
          }
          
          .letter-header {
            border-bottom: 3px solid #1e40af;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .company-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
          }
          
          .company-info {
            flex: 1;
          }
          
          .company-name {
            font-size: 24px;
            font-weight: 700;
            color: #1e40af;
            margin-bottom: 5px;
          }
          
          .company-details {
            font-size: 10px;
            color: #64748b;
            line-height: 1.5;
          }
          
          .offer-ref {
            background: #f1f5f9;
            padding: 15px;
            border-radius: 8px;
            border-left: 4px solid #1e40af;
            margin: 20px 0;
          }
          
          .ref-details {
            display: flex;
            gap: 30px;
            flex-wrap: wrap;
          }
          
          .ref-item {
            display: flex;
            flex-direction: column;
            min-width: 150px;
          }
          
          .ref-label {
            font-size: 9px;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 2px;
          }
          
          .ref-value {
            font-weight: 600;
            color: #1e293b;
            font-size: 11px;
          }
          
          .section {
            margin: 25px 0;
          }
          
          .section-title {
            font-size: 14px;
            font-weight: 600;
            color: #1e40af;
            margin-bottom: 12px;
            padding-bottom: 5px;
            border-bottom: 2px solid #e2e8f0;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            font-size: 11px;
          }
          
          th {
            background: #f8fafc;
            text-align: left;
            padding: 12px 15px;
            border: 1px solid #e2e8f0;
            font-weight: 600;
            color: #334155;
          }
          
          td {
            padding: 12px 15px;
            border: 1px solid #e2e8f0;
          }
          
          .signature-section {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
            gap: 40px;
            padding-top: 30px;
            border-top: 2px solid #e2e8f0;
          }
          
          .signature-box {
            flex: 1;
            text-align: center;
            min-height: 200px;
          }
          
          .signature-image {
            max-width: 200px;
            max-height: 80px;
            margin: 15px 0;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            padding: 5px;
            background: white;
          }
          
          .signature-line {
            height: 1px;
            background: #cbd5e1;
            margin: 30px auto;
            width: 80%;
          }
          
          .signature-details {
            font-size: 10px;
            color: #64748b;
            margin-top: 10px;
          }
          
          .signature-name {
            font-weight: 600;
            color: #1e293b;
            font-size: 11px;
            margin-top: 5px;
          }
          
          .acceptance-stamp {
            color: #059669;
            font-weight: 600;
            font-size: 10px;
            background: #d1fae5;
            padding: 5px 10px;
            border-radius: 4px;
            display: inline-block;
            margin-top: 5px;
          }
          
          .footer-note {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #e2e8f0;
            font-size: 9px;
            color: #94a3b8;
            text-align: center;
          }
        </style>

      </head>
      <body>
        <!-- PDF content here -->
      </body>
      </html>
    `;
  };

  const sendAcceptanceConfirmation = async () => {
    try {
      console.log('✅ Offer accepted successfully!');
    } catch (error) {
      console.log('Confirmation may not have been sent:', error);
    }
  };

  // Handle offer rejection
  const handleRejectOffer = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/offer-letters/${offerLetterId}/reject`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejectReason,
          candidateName: offerLetter?.candidateName,
          candidateEmail: offerLetter?.candidateEmail
        })
      });

      if (!response.ok) {
        throw new Error(`Server error ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setOfferLetter(prev => ({
          ...prev,
          status: 'rejected'
        }));
        
        setShowRejectModal(false);
        setRejectReason('');
        alert('Offer declined successfully. Thank you for your consideration.');
      } else {
        throw new Error(result.message || 'Failed to reject offer');
      }
      
    } catch (error) {
      console.error('Error declining offer:', error);
      alert('Error declining offer. Please contact HR directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle declaration checkbox change
  const handleDeclarationChange = (field) => {
    setDeclaration(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Loading State
  if (loading) {
    return (
      <div className="professional-offer-container">
        <div className="loading-overlay">
          <div className="loading-content">
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 150"><path fill="none" stroke="#FF156D" stroke-width="15" stroke-linecap="round" stroke-dasharray="300 385" stroke-dashoffset="0" d="M275 75c0 31-27 50-50 50-58 0-92-100-150-100-28 0-50 22-50 50s23 50 50 50c58 0 92-100 150-100 24 0 50 19 50 50Z"><animate attributeName="stroke-dashoffset" calcMode="spline" dur="2" values="685;-685" keySplines="0 0 1 1" repeatCount="indefinite"></animate></path></svg>            <h3>Loading Your Offer Letter</h3>
            <p>Please wait while we fetch your appointment details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="professional-offer-container">
        <div className="error-overlay">
          <div className="error-content">
            <div className="error-icon">❌</div>
            <h2>Unable to Load Offer Letter</h2>
            <p className="error-message">{error}</p>
            <div className="error-actions">
              <button onClick={() => window.location.reload()} className="retry-btn">
                🔄 Try Again
              </button>
              <p className="contact-support">
                Need help? Contact HR at: 
                <br />
                <strong>{offerLetter?.companyData?.hrEmail || offerLetter?.companyData?.companyEmail || 'hr@company.com'}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!offerLetter) {
    return (
      <div className="professional-offer-container">
        <div className="error-overlay">
          <div className="error-content">
            <div className="error-icon">📄</div>
            <h2>Offer Letter Not Found</h2>
            <p>The offer letter you're looking for doesn't exist or has expired.</p>
          </div>
        </div>
      </div>
    );
  }

  const offerData = offerLetter.offerData || {};
  const companyData = offerLetter.companyData || {};
  const benefits = offerLetter.benefits || {};
  const leavePolicy = offerLetter.leavePolicy || {};

  return (
    <div className="professional-offer-container">
      {/* Header */}
      <header className="offer-header">
        <div className="header-content">
          <div className="company-branding">
            {companyData.companyLogo && (
              <img 
                src={companyData.companyLogo} 
                alt="Company Logo" 
                className="company-logo"
              />
            )}
            <div className="company-text">
              <h1 className="company-name">{companyData.companyName}</h1>
              <p className="company-tagline">Letter of Appointment</p>
            </div>
          </div>
          
          <div className="offer-status-indicator">
            <div className={`status-badge ${offerLetter.status}`}>
              {offerLetter.status === 'sent_to_candidate' && '📧 Awaiting Response'}
              {offerLetter.status === 'accepted' && '✅ Accepted'}
              {offerLetter.status === 'rejected' && '❌ Declined'}
            </div>
            <p className="expiry-note">
              Valid until: {offerLetter.expiresAt}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="offer-main-content">
        {/* Navigation Tabs */}
        <div className="content-tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📋 Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'compensation' ? 'active' : ''}`}
            onClick={() => setActiveTab('compensation')}
          >
            💰 Compensation
          </button>
          <button 
            className={`tab-btn ${activeTab === 'benefits' ? 'active' : ''}`}
            onClick={() => setActiveTab('benefits')}
          >
            🏥 Benefits
          </button>
          <button 
            className={`tab-btn ${activeTab === 'action' ? 'active' : ''}`}
            onClick={() => setActiveTab('action')}
          >
            ✍️ Take Action
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content overview-tab">
            <div className="welcome-section">
              <h2>Welcome, {offerData.candidateName}!</h2>
              <p className="welcome-message">
                Congratulations! We are pleased to extend this offer for the position of 
                <strong> {offerData.position}</strong> at {companyData.companyName}. 
                Please review all details carefully.
              </p>
            </div>

            <div className="quick-overview">
              <div className="overview-grid">
                <div className="overview-card">
                  <div className="card-icon">🎯</div>
                  <h4>Position</h4>
                  <p>{offerData.position}</p>
                  <span className="card-subtext">{offerData.department}</span>
                </div>
                
                <div className="overview-card">
                  <div className="card-icon">📅</div>
                  <h4>Joining Date</h4>
                  <p>{offerLetter.joiningDate}</p>
                  <span className="card-subtext">{offerData.probationPeriod} probation</span>
                </div>
                
                <div className="overview-card">
                  <div className="card-icon">💰</div>
                  <h4>Monthly Salary</h4>
                  <p>{formatCurrency(offerData.salary)}</p>
                  <span className="card-subtext">Gross per month</span>
                </div>
                
                <div className="overview-card">
                  <div className="card-icon">📍</div>
                  <h4>Work Location</h4>
                  <p>{offerData.workLocation || 'To be specified'}</p>
                  <span className="card-subtext">{offerData.workingHours || 'Standard hours'}</span>
                </div>
              </div>
            </div>

            <div className="details-section">
              <h3>Offer Details</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Employee Code:</span>
                  <span className="detail-value">{offerData.employeeCode}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Reporting Manager:</span>
                  <span className="detail-value">{offerData.reportingManager || 'To be assigned'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Notice Period:</span>
                  <span className="detail-value">{offerData.noticePeriod}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Offer Reference:</span>
                  <span className="detail-value">{offerData.offerReference}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Saturday Schedule:</span>
                  <span className="detail-value">{offerData.saturdayHours}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compensation Tab */}
        {activeTab === 'compensation' && (
          <div className="tab-content compensation-tab">
            <h3>Compensation Details</h3>
            
            <div className="salary-breakdown-section">
              <div className="salary-card main">
                <h4>💰 Monthly Gross Salary</h4>
                <div className="salary-amount">{formatCurrency(offerData.salary)}</div>
                <div className="salary-period">Per month (Gross)</div>
              </div>
              
              <div className="salary-card">
                <h4>📊 Annual CTC</h4>
                <div className="salary-amount">{formatCurrency(offerData.ctc)}</div>
                <div className="salary-period">Cost to Company</div>
              </div>
            </div>

            {/* Salary Components */}
            {offerData.basicSalary && (
              <div className="salary-components">
                <h4>Salary Components</h4>
                <div className="components-grid">
                  {offerData.basicSalary && (
                    <div className="component-item">
                      <span className="component-label">Basic Salary</span>
                      <span className="component-value">{offerData.basicSalary}</span>
                    </div>
                  )}
                  {offerData.hra && (
                    <div className="component-item">
                      <span className="component-label">HRA</span>
                      <span className="component-value">{offerData.hra}</span>
                    </div>
                  )}
                  {offerData.specialAllowance && (
                    <div className="component-item">
                      <span className="component-label">Special Allowance</span>
                      <span className="component-value">{offerData.specialAllowance}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Benefits Tab */}
        {activeTab === 'benefits' && (
          <div className="tab-content benefits-tab">
            <h3>Benefits & Perks</h3>
            
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">🏦</div>
                <div className="benefit-content">
                  <h4>Provident Fund</h4>
                  <p>{benefits.pfContribution}</p>
                </div>
              </div>
              
              <div className="benefit-card">
                <div className="benefit-icon">🏥</div>
                <div className="benefit-content">
                  <h4>ESI Contribution</h4>
                  <p>{benefits.esiContribution}</p>
                </div>
              </div>
              
              <div className="benefit-card">
                <div className="benefit-icon">🩺</div>
                <div className="benefit-content">
                  <h4>Medical Coverage</h4>
                  <p>{benefits.medicalCoverage}</p>
                </div>
              </div>
              
              {benefits.gratuity && (
                <div className="benefit-card">
                  <div className="benefit-icon">💰</div>
                  <div className="benefit-content">
                    <h4>Gratuity</h4>
                    <p>{benefits.gratuity}</p>
                  </div>
                </div>
              )}
              
              {benefits.insurance && (
                <div className="benefit-card">
                  <div className="benefit-icon">🛡️</div>
                  <div className="benefit-content">
                    <h4>Insurance</h4>
                    <p>{benefits.insurance}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Leave Policy */}
            {(leavePolicy.sickLeave || leavePolicy.earnedLeave) && (
              <div className="leave-policy-section">
                <h4>Leave Policy</h4>
                <div className="leave-grid">
                  {leavePolicy.sickLeave && (
                    <div className="leave-item">
                      <div className="leave-type">Sick Leave</div>
                      <div className="leave-details">{leavePolicy.sickLeave}</div>
                    </div>
                  )}
                  {leavePolicy.earnedLeave && (
                    <div className="leave-item">
                      <div className="leave-type">Earned Leave</div>
                      <div className="leave-details">{leavePolicy.earnedLeave}</div>
                    </div>
                  )}
                  {leavePolicy.casualLeave && (
                    <div className="leave-item">
                      <div className="leave-type">Casual Leave</div>
                      <div className="leave-details">{leavePolicy.casualLeave}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Tab */}
        {activeTab === 'action' && (
          <div className="tab-content action-tab">
            <div className="action-header">
              <h3>Finalize Your Offer</h3>
              <p>Review and sign the document to proceed</p>
            </div>

            {/* HR Signature Preview */}
            {offerLetter.hrSignature && (
              <div className="hr-signature-section">
                <h4>HR Signature</h4>
                <div className="signature-preview-container">
                  <img src={offerLetter.hrSignature} alt="HR Signature" className="hr-signature-image" />
                  <div className="signature-info">
                    <p className="signature-name">{companyData.authorizedSignatory}</p>
                    <p className="signature-designation">{companyData.signatoryDesignation}</p>
                    <p className="signature-company">{companyData.companyName}</p>
                    <span className="signature-statuss">✅ Digitally Signed</span>
                  </div>
                </div>
              </div>
            )}

            {/* Candidate Signature */}
            <div className="candidate-signature-section">
              <h4>Your Signature</h4>
              {candidateSignature ? (
                <div className="signature-preview-container">
                  <img src={candidateSignature} alt="Your Signature" className="candidate-signature-image" />
                  <button 
                    onClick={() => setShowSignaturePad(true)}
                    className="change-signature-btn"
                  >
                    ✏️ Change Signature
                  </button>
                  <span className="signature-status">✅ Signature Added</span>
                </div>
              ) : (
                <div className="signature-required">
                  <button 
                    onClick={() => setShowSignaturePad(true)}
                    className="add-signature-btn"
                  >
                    ✍️ Add Your Signature
                  </button>
                  <p className="signature-instruction">Click to add your digital signature</p>
                </div>
              )}
            </div>

            {/* Declarations */}
            <div className="declarations-section">
              <h4>Declarations</h4>
              <div className="declaration-list">
                <div className="declaration-item">
                  <input 
                    type="checkbox" 
                    id="termsAccepted"
                    checked={declaration.termsAccepted}
                    onChange={() => handleDeclarationChange('termsAccepted')}
                  />
                  <label htmlFor="termsAccepted">
                    I accept all the terms and conditions mentioned in this appointment letter
                  </label>
                </div>
                <div className="declaration-item">
                  <input 
                    type="checkbox" 
                    id="backgroundCheck"
                    checked={declaration.backgroundCheck}
                    onChange={() => handleDeclarationChange('backgroundCheck')}
                  />
                  <label htmlFor="backgroundCheck">
                    I understand this offer is contingent upon satisfactory background verification
                  </label>
                </div>
                <div className="declaration-item">
                  <input 
                    type="checkbox" 
                    id="confidentiality"
                    checked={declaration.confidentiality}
                    onChange={() => handleDeclarationChange('confidentiality')}
                  />
                  <label htmlFor="confidentiality">
                    I agree to maintain strict confidentiality of company information
                  </label>
                </div>
                <div className="declaration-item">
                  <input 
                    type="checkbox" 
                    id="documentAuthenticity"
                    checked={declaration.documentAuthenticity}
                    onChange={() => handleDeclarationChange('documentAuthenticity')}
                  />
                  <label htmlFor="documentAuthenticity">
                    I declare that all documents and information provided are authentic
                  </label>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons-section">
              <div className="primary-actions">
                <button 
                  onClick={downloadPDF}
                  className="download-btn"
                  disabled={downloadingPDF}
                >
                  {downloadingPDF ? '⏳ Generating PDF...' : '📥 Download PDF'}
                </button>
                
                <button 
                  onClick={handleAcceptOffer}
                  className="accept-btn"
                  disabled={!candidateSignature || 
                    !declaration.termsAccepted || 
                    !declaration.backgroundCheck || 
                    !declaration.confidentiality || 
                    !declaration.documentAuthenticity ||
                    offerLetter.status === 'accepted' ||
                    isSubmitting}
                >
                  {isSubmitting ? '⏳ Processing...' : '✅ Accept Offer'}
                </button>
              </div>
              
              <button 
                onClick={() => setShowRejectModal(true)}
                className="reject-btn"
                disabled={offerLetter.status === 'accepted' || offerLetter.status === 'rejected'}
              >
                ❌ Decline Offer
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="offer-footer">
        <div className="contact-info">
          <h4>Contact HR</h4>
          <div className="contact-details">
            <p><strong>Email:</strong> {companyData.hrEmail || companyData.companyEmail}</p>
            <p><strong>Phone:</strong> {companyData.companyPhone}</p>
            <p><strong>Address:</strong> {companyData.registeredOffice}</p>
          </div>
        </div>
      </footer>

      {/* Signature Modal */}
      {showSignaturePad && (
        <ProfessionalSignaturePad 
          onSave={setCandidateSignature}
          onClose={() => setShowSignaturePad(false)}
          candidateName={offerData.candidateName}
        />
      )}

      {/* Accept Confirmation Modal */}
      {showAcceptConfirmation && (
        <div className="confirmation-modal">
          <div className="confirmation-content">
            <div className="confirmation-header">
              <div className="confirmation-icon">🎉</div>
              <h3>Offer Accepted Successfully!</h3>
            </div>
            <div className="confirmation-body">
              <p>Your signed offer letter has been sent to HR.</p>
              <p>You will receive onboarding details shortly.</p>
              <p><strong>Next Steps:</strong></p>
              <ul>
                <li>📧 Check your email for confirmation</li>
                <li>📄 Keep a copy of the signed document</li>
                <li>📋 Complete pre-joining formalities</li>
                <li>👔 Prepare for your joining date: {offerLetter.joiningDate}</li>
              </ul>
            </div>
            <div className="confirmation-actions">
              <button 
                onClick={() => setShowAcceptConfirmation(false)}
                className="confirmation-ok-btn"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="reject-modal">
          <div className="reject-modal-content">
            <div className="reject-modal-header">
              <h3>Decline Offer</h3>
              <button onClick={() => setShowRejectModal(false)} className="close-reject-btn">×</button>
            </div>
            <div className="reject-modal-body">
              <p>Please provide a reason for declining this offer:</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter your reason here..."
                rows="4"
              />
            </div>
            <div className="reject-modal-actions">
              <button 
                onClick={() => setShowRejectModal(false)}
                className="cancel-reject-btn"
              >
                Cancel
              </button>
              <button 
                onClick={handleRejectOffer}
                className="confirm-reject-btn"
                disabled={!rejectReason.trim() || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Confirm Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateOfferLetter;